/**
 * EntityHeader — Zona 1 do layout de 3 zonas (cabeçalho compacto).
 *
 * Onda 1 (fidelidade Bloco D): emite as classes canônicas `.eh` de
 * `components.css` (byte-a-byte do mockup Fase 1) ao invés de
 * styled-components próprios. Fidelidade pixel-perfect por construção.
 * Só o dropdown de ações (kebab) permanece styled — não há equivalente
 * canônico.
 *
 * API preservada (props):
 * - `onBack`, `backLabel`
 * - `leading`: ReactNode (ex.: avatar) — renderizado antes do título
 * - `title`, `subtitle`
 * - `badges`: [{ label, tone, key, icon? }] — chips ao lado do título
 * - `primaryAction`: { label, onClick, icon? } → botão `.btn--primary`
 * - `actions`: [{ label, onClick, icon?, danger?, primary? }] → kebab `[⋯]`
 * - `sticky` (boolean, default true) — gruda no topo ao rolar
 * - `standalone` (boolean, default true) — `true`: card com raio + borda
 *   completos. `false`: conecta visualmente com `.kpi-strip-v2` logo abaixo
 *   (sem borda inferior, raio só no topo).
 */
import React, { useEffect, useRef, useState } from "react";
import { FiArrowLeft, FiMoreVertical } from "react-icons/fi";

import useMediaQuery from "../../hooks/useMediaQuery";
import { mq } from "../../styles/canonical/breakpoints";
import { MenuItem, MenuList, MenuWrapper } from "./styles.jsx";

function EntityHeader({
  onBack,
  backLabel = "Voltar",
  leading,
  title,
  subtitle,
  badges,
  primaryAction,
  actions,
  sticky = true,
  standalone = true,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // BUG-B1 (auditoria 2026-05-23): auto-extrai action com `primary: true`
  // para botão visível; demais vão pro kebab.
  const _primaryFromActions =
    !primaryAction && Array.isArray(actions)
      ? actions.find((a) => a && a.primary)
      : null;
  const _effectivePrimary = primaryAction || _primaryFromActions;
  const _secondaryActions = Array.isArray(actions)
    ? actions.filter((a) => a && a !== _primaryFromActions)
    : actions;

  useEffect(() => {
    if (!menuOpen) return undefined;
    function onClickFora(event) {
      if (!menuRef.current?.contains(event.target)) setMenuOpen(false);
    }
    function onKey(event) {
      if (event.key === "Escape") setMenuOpen(false);
    }
    document.addEventListener("mousedown", onClickFora);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClickFora);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  const hasBadges = Array.isArray(badges) && badges.length > 0;

  // Auditoria mobile 2026-09-24 (S1): no celular o cabeçalho empilha
  // (regras em responsive-additions.css) e fica alto — em "Materiais a
  // cadastrar" chegava a 522px grudados no topo, cobrindo 63% da tela ao
  // rolar. Abaixo de 768px o sticky desliga; o inline style não pode ser
  // vencido por @media, então a decisão fica aqui.
  const isMobile = useMediaQuery(mq.mobile);

  // sticky + margin são layout (não cor) — inline preserva comportamento
  // sem poluir o CSS canônico.
  const wrapperStyle = {
    marginBottom: 16,
    ...(sticky && !isMobile ? { position: "sticky", top: 0, zIndex: 5 } : null),
  };

  return (
    <header
      className={`eh${standalone ? " eh--standalone" : ""}`}
      style={wrapperStyle}
    >
      {onBack && (
        <button type="button" className="eh__back" onClick={onBack}>
          <FiArrowLeft size={14} aria-hidden="true" />
          {backLabel}
        </button>
      )}

      {leading}

      <div className="eh__id">
        <div className="eh__id-top">
          {title && (
            <h1 className="eh__title" title={typeof title === "string" ? title : undefined}>
              {title}
            </h1>
          )}
          {hasBadges &&
            badges.map((b, idx) => (
              <span
                key={b.key || `${b.label}-${idx}`}
                className={`badge ${b.tone || "neutral"}${b.icon ? " no-dot" : ""}`}
              >
                {b.icon}
                {b.label}
              </span>
            ))}
        </div>
        {subtitle && (
          <div className="eh__sub" title={typeof subtitle === "string" ? subtitle : undefined}>
            {subtitle}
          </div>
        )}
      </div>

      <div className="eh__actions">
        {_effectivePrimary && (
          <button
            type="button"
            className="btn btn--primary"
            onClick={_effectivePrimary.onClick}
            // P2 Produção: primaryAction aceita `disabled` (+ `title` de
            // contexto) — ex.: "Criar lote com selecionados" sem seleção.
            // Retrocompatível: undefined preserva o comportamento antigo.
            disabled={Boolean(_effectivePrimary.disabled)}
            title={_effectivePrimary.title}
            style={
              _effectivePrimary.disabled
                ? { opacity: 0.5, cursor: "not-allowed" }
                : undefined
            }
          >
            {_effectivePrimary.icon}
            {_effectivePrimary.label}
          </button>
        )}
        {Array.isArray(_secondaryActions) && _secondaryActions.length > 0 && (
          <MenuWrapper ref={menuRef}>
            <button
              type="button"
              className="eh__kebab"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-label="Mais ações"
              onClick={() => setMenuOpen((v) => !v)}
            >
              <FiMoreVertical size={16} aria-hidden="true" />
            </button>
            {menuOpen && (
              <MenuList role="menu">
                {_secondaryActions.map((action, idx) => (
                  <MenuItem
                    key={action.key || `${action.label}-${idx}`}
                    $danger={action.danger}
                    role="none"
                  >
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setMenuOpen(false);
                        action.onClick?.();
                      }}
                    >
                      {action.icon}
                      {action.label}
                    </button>
                  </MenuItem>
                ))}
              </MenuList>
            )}
          </MenuWrapper>
        )}
      </div>
    </header>
  );
}

export default EntityHeader;
